import { Select } from '@napaicp/ui'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSession } from '@/features/session/use-session'
import { queueRoleSwitchAnnouncement } from '@/lib/role-switch-announcement'
import { availableRoleViews, type RoleView } from '@/lib/role-routing'
import { useCompetitions } from './api'

/**
 * PR-267/UX-268/UX-269 (Student Participant UI/UX/IA v4.45): the
 * Competition-scoped "目前角色" (Current Role) Context Control.
 *
 * Lets an account that holds more than one role on the competition it is
 * currently viewing (e.g. `competition_admin` and `student` on the same
 * competition) jump straight to the other role's screen, instead of the
 * account being stuck on whichever role `homeForCompetition` picked first —
 * there was previously no way to see the other side at all short of hand-
 * editing the URL (production QA: "一個帳號多個角色間如何切換?").
 *
 * Renders nothing unless the current URL is scoped to one competition
 * (`competitionId` present) — mirrors `CompetitionSwitcher` and, via
 * `RootLayout`'s `isCompetitionScopedPath` whitelist, guarantees `/home`
 * never shows this control (BR-204/AC-312: `/home`'s multi-role aggregation
 * must not require picking a role first).
 *
 * AC-309/AC-311: the current role must always be visible as readable text —
 * a single-role account gets a non-interactive "目前角色：X" label (never a
 * dropdown with no other option to pick), and a multi-role account gets an
 * accessible native `<select>` whose closed-state text is that same "目前
 * 角色：X" sentence, mirroring `CompetitionSwitcher`'s "目前競賽：{name}"
 * convention for the selected option. Switching only navigates (PR-267:
 * SHALL NOT grant/revoke role, SHALL NOT mutate authorization) — the target
 * route re-derives its own server-authoritative context on load.
 */
export function RoleSwitcher() {
  const { t } = useTranslation('nav')
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ competitionId?: string }>()
  const competitions = useCompetitions()
  const session = useSession()

  const competitionId = params.competitionId
  if (competitionId === undefined) {
    return null
  }

  const globalRole = session.data?.user?.globalRole ?? 'user'
  const competitionRoles =
    competitions.data?.find((entry) => entry.id === competitionId)?.roles ?? []
  const views = availableRoleViews(globalRole, competitionRoles, competitionId)
  if (views.length === 0) {
    return null
  }

  const currentView = views.find((entry) => location.pathname.startsWith(pathPrefix(entry.view)))

  const soleView = views.length === 1 ? views[0] : undefined
  if (soleView !== undefined) {
    // AC-311: exactly one reachable workspace on this competition — a
    // dropdown with a single, already-selected option would just be a
    // decoy control, so this renders as plain, non-interactive text
    // instead. Still visible (AC-309), never hidden.
    return (
      <span className="text-sm text-muted-foreground" data-testid="current-role-static">
        {t('currentRole', { role: viewLabel(t, soleView.view) })}
      </span>
    )
  }

  return (
    <Select
      aria-label={t('role')}
      className="w-full min-w-0 sm:w-40"
      value={currentView?.view ?? ''}
      onChange={(event) => {
        const nextView = views.find((entry) => entry.view === event.target.value)
        if (nextView !== undefined) {
          // UX-269: queued before navigating, consumed once the target
          // route lands — see `role-switch-announcement.ts` for why this
          // cannot just be React state.
          queueRoleSwitchAnnouncement(
            t('roleSwitchAnnouncement', { role: viewLabel(t, nextView.view) }),
          )
          void navigate(nextView.path)
        }
      }}
    >
      {currentView === undefined ? <option value="">{t('role')}</option> : null}
      {views.map(({ view }) => {
        const isSelected = currentView?.view === view
        const label = viewLabel(t, view)
        return (
          <option key={view} value={view}>
            {isSelected ? t('currentRole', { role: label }) : label}
          </option>
        )
      })}
    </Select>
  )
}

function viewLabel(t: (key: string, options?: Record<string, unknown>) => string, view: RoleView) {
  return t(`views.${view}`, { defaultValue: view })
}

function pathPrefix(view: RoleView): string {
  switch (view) {
    case 'admin':
      return '/admin'
    case 'judge':
      return '/judge'
    case 'student':
      return '/student'
    case 'observer':
      return '/admin/competitions'
  }
}
